import React, { useEffect, useState } from 'react';
import { verifyBillingSuccess } from '@/api/billing';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';

interface VerifyResponse {
   verify: boolean;
   msg: string;
   transactionId: string;
   amount: number;
   paymentMethod: string;
   subscription: string;
   store: string;
}

const BillingSuccess = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [showLoading, setShowLoading] = useState(true);

   const { data, isLoading } = useQuery({
      queryKey: ['verify-billing', id],
      queryFn: async () => {
         const response = await verifyBillingSuccess(id as string);
         return response.data as VerifyResponse;
      },
      enabled: !!id,
      refetchInterval: (query) => {
         return query.state.data?.verify ? false : 3000;
      },
      refetchIntervalInBackground: true,
   });

   // Set minimum loading time
   useEffect(() => {
      const timer = setTimeout(() => {
         setShowLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
   }, []);

   useEffect(() => {
      if (data?.verify) {
         setTimeout(() => {
            // navigate('/subscription');
         }, 2000);
      }
   }, [data, navigate]);

   return (
      <Container>
         <Card>
            {isLoading || showLoading || !data?.verify ? (
               <>
                  <LoadingSpinner />
                  <Title>Verifying Payment</Title>
                  <Message>Please wait while we verify your payment...</Message>
               </>
            ) : (
               <>
                  <Checkmark />
                  <Title>Payment Successful!</Title>
                  <Message>
                     Your subscription has been activated. Redirecting to
                     subscription page...
                  </Message>

                  <Content>
                     <Item>
                        <ItemLabel>Transaction ID:</ItemLabel>
                        <ItemValue>
                           {data?.transactionId.toUpperCase().slice(-7)}
                        </ItemValue>
                     </Item>
                     <Item>
                        <ItemLabel>Amount:</ItemLabel>
                        <ItemValue>
                           {currencyFormat(data?.amount, true)}
                        </ItemValue>
                     </Item>
                     <Item>
                        <ItemLabel>Payment Method:</ItemLabel>
                        <ItemImage src="/gcashpay.webp" />
                        {/* <ItemValue>{data?.paymentMethod}</ItemValue> */}
                     </Item>
                     <Item>
                        <ItemLabel>Subscription:</ItemLabel>
                        <ItemValue>
                           {data?.subscription} Month
                           {Number(data?.subscription) > 1 && 's'}
                        </ItemValue>
                     </Item>

                     <Item>
                        <ItemLabel>Store:</ItemLabel>
                        <ItemValue>{data?.store}</ItemValue>
                     </Item>
                  </Content>

                  <Button onClick={() => navigate('/subscription')}>
                     Go to Subscription
                  </Button>
               </>
            )}
         </Card>
      </Container>
   );
};

export default BillingSuccess;

// Animations
const spin = keyframes`
   0% { transform: rotate(0deg); }
   100% { transform: rotate(360deg); }
`;

const checkmark = keyframes`
   0% { transform: scale(0); opacity: 0; }
   50% { transform: scale(1.2); }
   100% { transform: scale(1); opacity: 1; }
`;

const fadeIn = keyframes`
   from { opacity: 0; }
   to { opacity: 1; }
`;

// Styled Components
const Container = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   min-height: 100vh;
   background-color: ${colors.GRAY14};
   padding: 1rem;
`;

const Card = styled.div`
   background: var(--app-surface);
   padding: 2rem;
   border-radius: 1rem;
   box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
   text-align: center;
   max-width: 400px;
   width: 100%;
   animation: ${fadeIn} 0.5s ease-in;
`;

const LoadingSpinner = styled.div`
   width: 50px;
   height: 50px;
   border: 4px solid ${colors.GRAY14};
   border-top: 4px solid ${colors.PRIMARY};
   border-radius: 50%;
   animation: ${spin} 1s linear infinite;
   margin: 0 auto 1.5rem;
`;

const Checkmark = styled.div`
   width: 50px;
   height: 50px;
   border-radius: 50%;
   background: ${colors.SUCCESS};
   margin: 0 auto 1.5rem;
   position: relative;
   animation: ${checkmark} 0.5s ease-in-out;

   &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -60%) rotate(45deg);
      width: 12px;
      height: 24px;
      border: solid white;
      border-width: 0 3px 3px 0;
   }
`;

const Title = styled.h1`
   color: ${colors.GRAY1};
   font-size: 1.5rem;
   margin-bottom: 1rem;
   font-weight: 600;
`;

const Message = styled.p`
   color: ${colors.GRAY4};
   font-size: 1rem;
   line-height: 1.5;
`;

const Button = styled.button`
   margin-top: 1rem;
   outline: none;
   border: none;
   background: none;
   color: ${colors.PRIMARY};
   padding: 0.5rem 1rem;
   text-decoration: underline;
`;

// Content
// Item
// ItemLabel
// ItemValue

const Content = styled.div`
   margin-top: 1rem;
`;

const Item = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 0.5rem 0;
   /* margin-bottom: 1rem; */
   /* padding: 0.5rem 0; */
   border-bottom: 1px solid ${colors.GRAY14};
`;

const ItemLabel = styled.p`
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const ItemValue = styled.p`
   font-weight: 400;
   color: ${colors.GRAY4};
`;

const ItemImage = styled.img`
   width: 20px;
   height: 20px;
   border-radius: 50%;
`;
